import { useState } from 'react';
import type { AiOperationDto } from '@scoutaround/script-breakdown-shared';

import { StatusPill, type WorkState } from '../ui/StatusPill.js';
import { AiCostApproval } from './AiCostApproval.js';
import { creditAmount } from './format.js';
import { untrackAiOperation, useTrackedAiOperations } from './sessionMemory.js';
import { useAiOperation } from './useAiOperation.js';
import { useProjectCredits } from './useProjectCredits.js';

import '../../styles/credits.css';

/** The outcome in words, with its place in the workspace's status vocabulary. */
function describe(operation: AiOperationDto): { state: WorkState; label: string; title: string; body: string; testId: string } {
  if (operation.status === 'CANCELLED') {
    return {
      state: 'EXCLUDED',
      label: 'Cancelled',
      title: 'Cancelled',
      body: 'Nothing was charged for it.',
      testId: 'ai-operation-cancelled',
    };
  }
  if (operation.status === 'EXPIRED') {
    return {
      state: 'EXCLUDED',
      label: 'Expired',
      title: 'The estimate expired before it was started',
      body: 'Nothing was reserved or charged.',
      testId: 'ai-operation-expired',
    };
  }
  if (operation.status === 'SETTLED') {
    if (operation.outcome === 'stopped_at_cap') {
      // An offer to go on, not a fault: amber, never the red of "needs attention" (B.7).
      return {
        state: 'READY_FOR_REVIEW',
        label: 'Stopped at your maximum',
        title: 'Stopped at the maximum you approved',
        body: 'Everything it finished is kept. The rest needs its own estimate and approval, and nothing more runs until you give it.',
        testId: 'ai-operation-stopped-at-cap',
      };
    }
    if (operation.outcome === 'partial') {
      return {
        state: 'COMPLETED',
        label: 'Completed in part',
        title: 'Operation completed in part',
        body: 'Some of the work could not be finished. Only what was delivered is charged, and it is kept.',
        testId: 'ai-operation-partial',
      };
    }
    if (operation.outcome === 'failed') {
      return {
        state: 'FAILED',
        label: 'Did not finish',
        title: 'The operation did not finish',
        body: 'Only what was delivered is charged. It can be run again.',
        testId: 'ai-operation-failed',
      };
    }

    return {
      state: 'COMPLETED',
      label: 'Completed',
      title: 'Operation completed',
      body: 'Its results are on the production now.',
      testId: 'ai-operation-completed',
    };
  }
  if (operation.status === 'SETTLEMENT_PENDING' || operation.status === 'RECONCILIATION_REQUIRED') {
    return {
      state: 'IN_PROGRESS',
      label: 'Working out the final cost',
      title: 'Finished — working out the final cost',
      body: `This can take a while. Nothing above the maximum you approved (${creditAmount(operation.capCents)}) can be charged.`,
      testId: 'ai-operation-settling',
    };
  }

  return {
    state: 'PROCESSING',
    label: 'Running',
    title: 'Running',
    body: `Up to ${creditAmount(operation.capCents)}, the maximum you approved, is set aside for it.`,
    testId: 'ai-operation-running',
  };
}

/**
 * WHAT AN AI OPERATION COST, ONCE IT HAS SETTLED (design B.8, S2b).
 *
 * Completed, completed in part, did not finish, or STOPPED AT THE MAXIMUM YOU
 * APPROVED — with the results kept and a Continue that estimates the rest
 * (`continuesOperationId`) and runs nothing until that estimate is approved.
 * Then the credits actually charged, what went back, the balance now, and the
 * way to the history.
 *
 * "Not settled yet" is not "charged nothing": the charged and released figures
 * are null until the operation settles, and are left out until then rather
 * than drawn as €0.00.
 */
export function AiOperationSummary({
  projectId,
  operationId,
  scriptVersionId,
  onDismiss,
  onOpenHistory,
  testId = 'ai-operation-summary',
}: {
  projectId: string;
  operationId: string;
  /** The version a continuation is estimated on. */
  scriptVersionId: string;
  onDismiss?: () => void;
  onOpenHistory?: () => void;
  testId?: string;
}) {
  const { operation, final, givenUp, error, refetch } = useAiOperation(projectId, operationId);
  const credits = useProjectCredits(projectId);
  const [continuing, setContinuing] = useState(false);

  if (operation === null) {
    if (error === null) {
      return (
        <div className="credits-summary-card" data-testid={testId} data-status="LOADING">
          <p className="mm credits-note" role="status" data-testid="ai-operation-loading">
            Checking the AI operation…
          </p>
        </div>
      );
    }

    return (
      <div className="credits-summary-card" data-testid={testId} data-status="UNKNOWN">
        <p className="mm credits-note" data-testid="ai-operation-error">
          Could not check this AI operation. Its charge, once settled, is in the credit history.
        </p>
        <div className="credits-actions">
          {onDismiss === undefined ? null : (
            <button type="button" className="ctl" data-testid="ai-operation-dismiss" onClick={onDismiss}>
              Dismiss
            </button>
          )}
          <button type="button" className="ctl" data-testid="ai-operation-recheck" onClick={refetch}>
            Check again
          </button>
        </div>
      </div>
    );
  }

  const said = describe(operation);
  const settled = operation.status === 'SETTLED';
  const mayContinue =
    settled && operation.outcome === 'stopped_at_cap' && operation.continuationOperationId === null;

  return (
    <div className="credits-summary-card" data-testid={testId} data-status={operation.status} data-outcome={operation.outcome ?? ''}>
      <div className="credits-summary-head">
        <p className="colh" style={{ margin: 0 }} data-testid="ai-operation-title">
          {operation.title}
        </p>
        <StatusPill state={said.state} label={said.label} size="sm" testId="ai-operation-status" />
      </div>

      <div data-testid={said.testId}>
        <p className="credits-line" style={{ fontWeight: 700 }}>
          {said.title}
        </p>
        <p className="mm credits-note">{said.body}</p>
      </div>

      {givenUp ? (
        <p className="mm credits-note" data-testid="ai-operation-given-up">
          This summary has stopped checking. The charge appears in the credit history once it is settled.
        </p>
      ) : null}

      <div className="credits-figures">
        {operation.chargedCents === null ? null : (
          <p className="credits-line" data-testid="ai-operation-charged">
            Credits charged:{' '}
            <strong>{operation.chargeBelowOneCent ? 'less than €0.01' : creditAmount(operation.chargedCents)}</strong>
          </p>
        )}
        {operation.releasedCents === null || operation.releasedCents === 0 ? null : (
          <p className="credits-line mm" data-testid="ai-operation-released">
            Returned to the production’s credits: {creditAmount(operation.releasedCents)}
          </p>
        )}
        {final && credits.data !== undefined ? (
          <p className="credits-line" data-testid="ai-operation-balance">
            Available now: <strong>{creditAmount(credits.data.availableCents)}</strong>
          </p>
        ) : null}
      </div>

      {settled && operation.outcome === 'stopped_at_cap' && operation.continuationOperationId !== null ? (
        <p className="mm credits-note" data-testid="ai-operation-continued">
          A continuation has already been estimated from this operation.
        </p>
      ) : null}

      {continuing ? (
        <AiCostApproval
          projectId={projectId}
          operationType={operation.operationType}
          scriptVersionId={scriptVersionId}
          continuesOperationId={operation.operationId}
          testId="ai-operation-continuation"
          onClose={() => {
            setContinuing(false);
          }}
          onStarted={() => {
            // The continuation is tracked on its own; this one is done.
            onDismiss?.();
          }}
        />
      ) : (
        <div className="credits-actions">
          {onOpenHistory === undefined ? null : (
            <button type="button" className="ctl" data-testid="ai-operation-history" onClick={onOpenHistory}>
              View credit history
            </button>
          )}
          {(final || givenUp) && onDismiss !== undefined ? (
            <button type="button" className="ctl" data-testid="ai-operation-dismiss" onClick={onDismiss}>
              Dismiss
            </button>
          ) : null}
          {mayContinue ? (
            <button
              type="button"
              className="btn btn-primary"
              data-testid="ai-operation-continue"
              onClick={() => {
                setContinuing(true);
              }}
            >
              Continue
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

/**
 * THE SUMMARIES OF THE AI OPERATIONS APPROVED IN THIS TAB, wherever the
 * producer is on this production.
 *
 * Mounted by the workspace shell and the setup flow, because the screen that
 * started an operation does not stay: every entry point is replaced by the
 * run's progress indicator once the run begins. Renders nothing until an
 * operation has been approved, and asks the server about nothing until then.
 */
export function AiOperationTray({
  projectId,
  onOpenHistory,
}: {
  projectId: string;
  onOpenHistory?: () => void;
}) {
  const tracked = useTrackedAiOperations(projectId);

  if (tracked.length === 0) return null;

  return (
    <div className="credits-tray" data-testid="ai-operation-tray">
      {tracked.map((entry) => (
        <AiOperationSummary
          key={entry.operationId}
          projectId={projectId}
          operationId={entry.operationId}
          scriptVersionId={entry.scriptVersionId}
          testId={`ai-operation-summary-${entry.operationId}`}
          onOpenHistory={onOpenHistory}
          onDismiss={() => {
            untrackAiOperation(projectId, entry.operationId);
          }}
        />
      ))}
    </div>
  );
}
